"use client";

import { useState } from "react";
import {
  AgentChatProvider,
  AgentFab,
  AgentMobileOverlay,
} from "@/components/agent";
import { BottomNavigation } from "@/components/navigation";
import { SettingsScreen } from "@/components/settings-view";
import { TradeScreen } from "@/components/trades-view";
import { QuotesScreen } from "@/components/quotes-view";
import { DesktopScreen } from "@/components/desktop";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";
import { useSymbolsStore } from "@/vybe-trader-client/symbols/stores/useSymbolsStore";
import { HistoryScreen } from "@/components/history-view";
import ChartScreen from "@/components/chart/ChartScreen";
import { useChartStore3 } from "@/vybe-trader-client/chart/stores/useChartStore3";
import { useIsDesktop } from "@/hooks/use-mobile";
import { useRestoreChartDesk } from "@/lib/chart-desk";

export default function Home() {
  const [activeTab, setActiveTab] = useState("quotes");
  const [shouldOpenTradeOrder, setShouldOpenTradeOrder] = useState(false);
  // The symbol the trade ticket must open on. Passed explicitly to TradeScreen: the legacy v1 chart
  // store it used to read is only ever seeded with the first watchlist symbol (EURUSD), so every
  // mobile "Trade" tap opened EURUSD regardless of the row tapped (SR Global bug 2026-09-08).
  const [tradeOrderSymbol, setTradeOrderSymbol] = useState<string | undefined>(undefined);
  const [agentOverlayOpen, setAgentOverlayOpen] = useState(false);
  // Render only ONE view (desktop OR mobile) at a time. Previously both were
  // mounted and toggled via CSS, which meant two independent `<Chart>` /
  // klinecharts instances were always running - each subscribing to live
  // quotes, scheduling rAFs, and creating overlays. On resize, the chart that
  // had been hidden (and initialized at 0x0) could end up out of sync with
  // the store. With a single mount we cut that work in half and remove the
  // sync hazard entirely.
  const isDesktop = useIsDesktop();

  // Proposal 74: replay the last chart desk (symbol + timeframe per panel) once
  // the symbol universe can resolve it. Mounted here, not in ChartScreen, so it
  // runs once per app load rather than once per panel — and never on the
  // detached chart popup route, where the URL params own the selection.
  useRestoreChartDesk(isDesktop);

  const allSubscribed = useSubscribedSymbolsStore(
    (state) => state.allSubscribed
  );

  const resolveSymbolForChart = (currency: string) => {
    const fromSubscribed = allSubscribed.find(
      (entry) => entry.symbol.currency === currency
    )?.symbol;
    if (fromSubscribed) return fromSubscribed;
    return useSymbolsStore.getState().getSymbolByCurrency(currency);
  };

  const handleNavigateToChart = (symbol: string) => {
    const sym = resolveSymbolForChart(symbol);
    if (sym) {
      useChartStore3.getState().setSelectedSymbol(sym, 0);
    }
    setActiveTab("chart");
  };

  const handleNavigateToTrade = (symbol: string) => {
    const sym = resolveSymbolForChart(symbol);
    if (sym) {
      useChartStore3.getState().setSelectedSymbol(sym, 0);
    }
    setTradeOrderSymbol(symbol);
    setShouldOpenTradeOrder(true);
    setActiveTab("trade");
  };

  const renderMobileContent = () => {
    switch (activeTab) {
      case "quotes":
        return (
          <QuotesScreen
            onNavigateToChart={handleNavigateToChart}
            onNavigateToTrade={handleNavigateToTrade}
          />
        );
      case "chart":
        return <ChartScreen isDesktop={false} />;
      case "trade":
        return (
          <TradeScreen
            onNavigateToChart={handleNavigateToChart}
            shouldOpenTradeOrder={shouldOpenTradeOrder}
            tradeOrderSymbol={tradeOrderSymbol}
            onTradeOrderClosed={() => setShouldOpenTradeOrder(false)}
          />
        );
      case "history":
        return (
          <HistoryScreen
            onNavigateToChart={handleNavigateToChart}
            onNavigateToTrade={handleNavigateToTrade}
          />
        );
      case "settings":
        return <SettingsScreen />;
      default:
        return (
          <QuotesScreen
            onNavigateToChart={handleNavigateToChart}
            onNavigateToTrade={handleNavigateToTrade}
          />
        );
    }
  };

  if (isDesktop) {
    return (
      <div className="h-dvh w-full max-w-full overflow-hidden">
        <DesktopScreen />
      </div>
    );
  }

  return (
    <AgentChatProvider>
      <div className="flex h-dvh w-full max-w-full flex-col overflow-hidden bg-background text-foreground pt-[env(safe-area-inset-top,0px)]">
        {/* No floating account chip on mobile (proposal 55) — the Vybe Trader
            identity/presence surface lives in Settings → Vybe Trader. */}
        <div className="min-h-0 flex-1 overflow-hidden">{renderMobileContent()}</div>
        <BottomNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      </div>
      <AgentFab
        onOpen={() => setAgentOverlayOpen(true)}
        dodgeChart={activeTab === "chart"}
      />
      <AgentMobileOverlay
        open={agentOverlayOpen}
        onOpenChange={setAgentOverlayOpen}
      />
    </AgentChatProvider>
  );
}
